import { useEffect, useRef, useState, useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { useDispatch, useSelector } from "react-redux";
import * as actions from "../../../../../actions/actions";
import axiosInstance from "../../../../../axios/axiosInstance";
import { Link } from "react-router-dom";
import { styled } from "@mui/material/styles";
import { encrypt } from "../../../../global/helperFunctions";
import PenaltySelectEditor from "./PenaltyEditor";
import FormControl from "@mui/material/FormControl";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import Tooltip from "@mui/material/Tooltip";
import { sailNumComparator } from "../../../../global/helperFunctions";
import StartTimeConfirmModal from "../../Finish-Time-Entry-New/StartTimeConfirmModal";

const timePenalties = ["ARB", "ENP", "ESP", "SCP", "ZFP", "RDG", "OCS", "DNF", "RET", "DSQ", "BFD", "UFD"];

const sortOptions = [
  { label: "Default", value: "position" },
  { label: "Sail No", value: "sailNum" },
  { label: "Fleet", value: "fleetName" },
];

const rowHeight = 55;
const headerHeight = 150;
const minHeight = 200;
const maxHeight = 280;

const formatTime = (time) => {
  const hours = time.getHours().toString().padStart(2, "0");
  const minutes = time.getMinutes().toString().padStart(2, "0");
  const seconds = time
    .getSeconds()
    .toString()
    .padStart(2, "0")
    // const milliseconds = Math.floor(time.getMilliseconds() / 10)
    .toString()
    .padStart(2, "0");

  return `${hours}:${minutes}:${seconds}`;
};

const getRowClass = (params) => {
  if (params.data.editable === true || params.data.editable === 1) {
    return "error-row";
  }
  return "";
};

const getRowStyle = () => ({
  // marginBottom: "10px",
  borderBottom: "3px solid var(--ty-theme-gradient-solid-color)",
});

const IOSSwitch = styled(Switch)(({ theme }) => ({
  width: 31.5,
  height: 19.5,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 1.5,
    transitionDuration: "300ms",
    "&.Mui-checked": {
      transform: "translateX(13px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: theme.palette.mode === "dark" ? "#2ECA45" : "#356DAD",
        opacity: 1,
        border: 0,
      },
      "&.Mui-disabled + .MuiSwitch-track": {
        opacity: 0.5,
      },
    },
    "&.Mui-focusVisible .MuiSwitch-thumb": {
      color: "#33cf4d",
      border: "6px solid #fff",
    },
    "&.Mui-disabled .MuiSwitch-thumb": {
      color:
        theme.palette.mode === "light"
          ? theme.palette.grey[100]
          : theme.palette.grey[600],
    },
    "&.Mui-disabled + .MuiSwitch-track": {
      opacity: theme.palette.mode === "light" ? 0.7 : 0.3,
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 16.5,
    height: 16.5,
  },
  "& .MuiSwitch-track": {
    borderRadius: 26 / 2,
    backgroundColor: theme.palette.mode === "light" ? "#E9E9EA" : "#39393D",
    opacity: 1,
    transition: theme.transitions.create(["background-color"], {
      duration: 500,
    }),
  },
}));

export default function RealTimeCapture(props) {
  const gridRefs = [useRef(null), useRef(null), useRef(null)];

  let unfinishedEntrants = useSelector(
    (state) =>
      state.initialFinishRaceEntrantsList.initialFinishRaceEntrantsList.data
  );
  let aboutFinishRaceEntrants = useSelector(
    (state) =>
      state.aboutFinishRaceEntrantsList.aboutFinishRaceEntrantsList.data
  );
  let completeFinishRaceEntrants = useSelector(
    (state) =>
      state.completeFinishRaceEntrantsList.completeFinishRaceEntrantsList.data
  );
  const [payload, setPayload] = useState([]);
  const [raceSessionURL, setRaceSessionURL] = useState("");
  const [snWithAlpha, setSNWithAlpha] = useState(true);
  const [onWaterEntrants, setOnWaterEntrants] = useState(false);
  const [timeZone, setTimeZone] = useState("");
  const [warnOneTime, setWarnOneTime] = useState(false);
  const [run, setRun] = useState(false);
  const [unfinishedSearchValue, setUnfinishedSearchValue] = useState("");
  const [enterCount, setEnterCount] = useState(0);
  const [lastEditedCell, setLastEditedCell] = useState(null);

  const [sortField, setSortField] = useState("position");
  const [isSortAsc, setIsSortAsc] = useState(false);

  const filteredUnfinishedEntrants = useMemo(() => {
    if (!onWaterEntrants) return unfinishedEntrants;
    return unfinishedEntrants.filter((entrant) => entrant.onWater === 1);
  }, [onWaterEntrants, unfinishedEntrants]);

  const handleOnWaterSwitchChange = (e) => {
    setOnWaterEntrants(e.target.checked);
  };

  const sortFn = (a, b) => {
    const field = sortField || "";
    let aVal, bVal;

    if (field === "time") {
      aVal = (+a?.hr || 0) * 3600 + (+a?.min || 0) * 60 + (+a?.sec || 0);
      bVal = (+b?.hr || 0) * 3600 + (+b?.min || 0) * 60 + (+b?.sec || 0);
    } else if (field === "place" || field === "position") {
      aVal = +a?.[field] || 0;
      bVal = +b?.[field] || 0;
    } else if (field === "sailNum") {
      return sailNumComparator(a?.sailNum, b?.sailNum, isSortAsc);
    } else {
      aVal = (a?.[field] || "").toString().toLowerCase();
      bVal = (b?.[field] || "").toString().toLowerCase();
    }

    const result = aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
    return isSortAsc ? result : -result;
  };

  const handleSort = () => [...completeFinishRaceEntrants].sort(sortFn);

  useEffect(() => {
    dispatch(actions.updateCompleteFinishRaceEntrants(handleSort()));
  }, [isSortAsc, sortField]);
  
  useEffect(() => {
    // Use unguarded reset so both true/false toggles will reset the sort
    if (props.changeSort !== undefined) {
      setSortField("position");
      setIsSortAsc(false);
    }
  }, [props.changeSort]);

  const systemLocale = navigator.language || "en-US"; // Fetch system locale

  const gridContext = {
    snWithAlpha: snWithAlpha,
    onWaterEntrants: onWaterEntrants,
    unfinishedEntrants: unfinishedEntrants,
    aboutFinishRaceEntrants: aboutFinishRaceEntrants,
    completeFinishRaceEntrants: completeFinishRaceEntrants,
    sessionId: props.session,
  };

  const refreshAllGrids = () => {
    gridRefs.forEach((gridRef, index) => {
      if (gridRef.current && gridRef.current.api) {
        // console.log(`Refreshing Grid ${index + 1}`);
        gridRef.current.api.refreshCells({ force: true });
      } else {
        console.warn(`GridRef ${index + 1} is not ready`);
      }
    });
  };

  useEffect(() => {
    setRaceSessionURL(
      "../../../raceday/race-session?eventId=" +
        props.event +
        "&sessDate=" +
        props.raceSessionsDate
    );
  }, [props.event, props.raceSession]);

  useEffect(() => {
    let time_zone = timeZone === "" ? "Australia/Canberra" : timeZone;

    if (!warnOneTime) {
      let timeZoneNotFound = false;
      time_zone = localStorage.getItem("time_zone");
      if (!time_zone) {
        time_zone = "Australia/Canberra";
        timeZoneNotFound = true;
      }
      setTimeZone(time_zone);
      if (timeZoneNotFound) {
        props.activateSnackbarTab("Time Zone has not been set", "error", true);
      }
      setWarnOneTime(true);
    }
  }, []);

  /* useEffect(() => {

    // if (aboutFinishRaceEntrants.length == 0) addRow();
    console.log(aboutFinishRaceEntrants)
      if (
      aboutFinishRaceEntrants &&
        aboutFinishRaceEntrants.length>0 &&
      aboutFinishRaceEntrants[aboutFinishRaceEntrants.length - 1]?.sailNum ===
        "" &&
      aboutFinishRaceEntrants[aboutFinishRaceEntrants.length - 1]?.boatName ===
        "" &&
      !nowPressed
    ) {
      setTimeout(() => {
        if (gridRefs[1].current && gridRefs[1].current.api) {
          // console.log("THE REF", gridRefs[1]);

          gridRefs[1].current.api.startEditingCell({
            rowIndex:
              aboutFinishRaceEntrants.length > 0
                ? aboutFinishRaceEntrants.length - 1
                : 0, // Index of new row
            colKey: "sailNum", // Column to focus on
          });
        }
      }, 100);
    }
  //  refreshAllGrids();
  }, [aboutFinishRaceEntrants]); */

  useEffect(() => {
    const postPublishPayload = async () => {
      try {
        if (payload.length !== 0) {
          // setResultProcessEnabledChange(false);
          const POST_PUBLISH_API = "api/HC/CalculateHandicap";
          const response = await axiosInstance.post(POST_PUBLISH_API, payload);
          console.log("RES2--", response);
          props.setProcessFlag(false);
          props.activateSnackbarTab(
            "Results Processed Successfully",
            "success",
            true
          );
        } else {
          props.setProcessFlag(false);
          throw new Error("Error occured in processing Results");
        }
      } catch (error) {
        props.setProcessFlag(false);
        props.activateSnackbarTab(error.response.data.message, "error", true);
        // setResultProcessEnabledChange(false);
      }
    };
    if (run) {
      postPublishPayload();
      setRun(false);
    }
  }, [run]);

  useEffect(() => {
    console.log("Updated aboutFinishRaceEntrants:", aboutFinishRaceEntrants);
    savePrefinished();
  }, [aboutFinishRaceEntrants]); // Runs when `aboutFinishRaceEntrants` changes

  const dispatch = useDispatch();

  const unfinishedColumnDefs = useMemo(
    () =>
      [
        {
          field: "sailNum",
          headerName: "PRELOAD",
          sortable: true,
          // flex: 1,
          minWidth: 100,
          maxWidth: 150,
          // suppressSizeToFit: true,
          comparator: sailNumComparator,
          cellRenderer: (params) => {
            const value = params.context.snWithAlpha
              ? params.value
              : params.value.replace(/[A-Za-z]/g, "");
            return (
              <Button
                size="small"
                sx={{
                  px: 2,
                  py: 0.25,
                  borderRadius: 10,
                  border: 1,
                  borderColor: "success.main",
                  color: "success.main",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textTransform: "none",
                  maxWidth: "100%",
                  width: "100%",
                }}
                onClick={() => handleBoxClick(params)}
                title={value}
              >
                <span
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    width: "100%",
                    textAlign: "center",
                  }}
                >
                  {value}
                </span>
              </Button>
            );
          },
          cellRendererParams: () => ({
            snWithAlpha, // Pass current state to the cellRenderer
          }),
        },
        {
          field: "fleetName",
          headerName: "FLEET",
          sortable: true,
          flex: 2,
          minWidth: 80,
          // maxWidth: 120,
          cellRenderer: (params) => (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                height: "100%",
                width: "100%",
              }}
            >
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  width: "100%",
                }}
                title={params.value || ''}
              >
                {params.value}
              </span>
            </div>
          ),
        },
        props?.caseOTBEntrant && {
          field: "boatName",
          headerName: "CLASS",
          flex: 2,
          minWidth: 100,
          // maxWidth: 200,
          sortable: true,
          cellRenderer: (params) => (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                height: "100%",
                width: "100%",
              }}
            >
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  width: "100%",
                }}
                title={params.value || ''}
              >
                {params.value}
              </span>
            </div>
          ),
        },
        !props?.caseOTBEntrant && {
          field: "boatName",
          headerName: "BOAT NAME",
          flex: 2,
          minWidth: 100,
          // maxWidth: 200,
          cellRenderer: (params) => (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                height: "100%",
                width: "100%",
              }}
            >
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  width: "100%",
                }}
                title={params.value || ''}
              >
                {params.value}
              </span>
            </div>
          ),
        },
        {
          field: "capture",
          headerName: "NOW",
          flex: 1,
          minWidth: 80,
          // maxWidth: 120,
          cellRenderer: (params) => {
            return (
              <Button
                variant="contained"
                onClick={() =>
                  handleNowClick(params.data.sailNum, params.data, params, true)
                }
                value={params.value}
                color="primary"
                className="ty_primary_btn finish_time_now_btn"
                size="small"
              >
                NOW
              </Button>
            );
          },
        },
      ].filter(Boolean),
    [props?.caseOTBEntrant]
  );

  const getRowClass = (params) => {
    // return params.data.errorHR ||
    //   params.data.errorMin ||
    //   params.data.errorSec ||
    //   params.data.editable === 1 ||
    //   params.data.editable === true
    //   ? "error-row"
    //   : "";
    if (
      params.data.errorHR ||
      params.data.errorMin ||
      params.data.errorSec ||
      params.data.editable === true ||
      params.data.editable === 1
    ) {
      return "error-row";
    }
    return "";
  };

  const finishedColumnDefs = [
    {
      field: "position",
      headerName: "POS",
      //sortable: true,
      //sort: "desc",
      hide: false,
      flex: 1,
      minWidth: 80,
      // maxWidth: 100,
      comparator: (valueA, valueB) => {
        if (valueA === undefined || valueA === null || valueA === "") return 1;
        if (valueB === undefined || valueB === null || valueB === "") return -1;
        return Number(valueA) - Number(valueB);
      },
    },
    {
      field: "place",
      headerName: "PLC",
      //sortable: true,
      hide: true,
      flex: 2,
      minWidth: 50,
      // maxWidth: 100,
      //sort: "desc", // Set default sorting to descending
    },
    {
      field: "sailNum",
      headerName: "SAIL NO",
      filter: "agTextColumnFilter",
      floatingFilter: false,
      suppressMenu: true,
      flex: 2,
      minWidth: 100,
      maxWidth: 200,
      comparator: sailNumComparator,
      valueParser: (params) => params.newValue?.toUpperCase() || "",
      //sortable: true,
      editable: (params) => !params.data?.boatName,
      cellClassRules: {
        error: (params) =>
          !params.value || params.value.trim() === "" || !params.data?.boatName,
        "highlight-cell": (params) =>
          params.data?.boatName !== null && params.data?.boatName !== "",
      },
      cellStyle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        overflow: 'hidden',
      },
      cellRenderer: (params) => {
        const isManualEntry = !params.data?.boatName || params.data?.boatName === "";
        return (
          <div
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%',
              // padding: '0 12px',
              fontWeight: isManualEntry ? '700' : 'normal',
            }}
            title={params.value || ''}
          >
            {params.value || ''}
          </div>
        );
      },

      onCellValueChanged: (event) => {
        if (!event.newValue) {
          setTimeout(() => {
            props.activateSnackbarTab(
              "Please define sail number",
              "error",
              true
            );
          }, 300);
          // Reset the incorrect edit
          event.data.sailNum = event.oldValue;

          const sailIndx = event.node.rowIndex;
          console.log("THE INDX", sailIndx);

          // Refocus the cell
          setTimeout(() => {
            const gridApi = gridRefs[2]?.current?.api;
            if (gridApi && sailIndx !== -1) {
              gridApi.ensureIndexVisible(sailIndx);

              gridApi.setFocusedCell(sailIndx, "sailNum");
              gridApi.startEditingCell({
                rowIndex: sailIndx,
                colKey: "sailNum",
              });
            }
          }, 100);

          return;
        }

        console.log("Value changed in column:", event.colDef.field);
        console.log("Old Value:", event.oldValue, "New Value:", event.newValue);
        console.log("ROWW:", event);
        console.log("ALLL", props.allUnfinished);
        unfinishedEntrants = event.context.unfinishedEntrants;
        completeFinishRaceEntrants = event.context.completeFinishRaceEntrants;

        // Find the correct row index manually
        const currentRowIndex = completeFinishRaceEntrants.findIndex(
          (x) => x.sailNum === event.data.sailNum
        );

        // Duplicate row prevention
        if (
          event.newValue !== "" &&
          (completeFinishRaceEntrants.filter(
            (x) => x.sailNum.toLowerCase() === event.newValue.toLowerCase()
          ).length >= 2 ||
            aboutFinishRaceEntrants.filter(
              (x) =>
                x.sailNum?.trim() &&
                x.sailNum.trim().toLowerCase() ===
                  event.newValue.trim().toLowerCase()
            ).length >= 1)
        ) {
          console.log("TEST A1");

          setTimeout(() => {
            props.activateSnackbarTab(
              `Sail Number ${event.newValue} already added!`,
              "error",
              true
            );
          }, 300);

          event.data.sailNum = event.oldValue ? event.oldValue : "";

          setTimeout(() => {
            if (gridRefs[2].current && gridRefs[2].current.api) {
              gridRefs[2].current.api.ensureIndexVisible(currentRowIndex); // Scroll to the correct row

              gridRefs[2].current.api.setFocusedCell(
                currentRowIndex,
                "sailNum"
              ); // Focus on the "sailNum" column

              gridRefs[2].current.api.startEditingCell({
                rowIndex: currentRowIndex, // Correct row index
                colKey: "sailNum", // Column to focus on
              });
            }
          }, 100);

          return;
        }

        if (event.newValue === "") {
          props.activateSnackbarTab("Please define sail number", "error", true);
          setTimeout(() => {
            props.activateSnackbarTab(
              "Please define sail number",
              "error",
              true
            );
          }, 300);
          // Reset the incorrect edit
          event.data.sailNum = event.oldValue;

          const sailIndx = event.node.rowIndex;
          console.log("THE INDX", sailIndx);

          // Refocus the cell
          setTimeout(() => {
            const gridApi = gridRefs[2]?.current?.api;
            if (gridApi && sailIndx !== -1) {
              gridApi.ensureIndexVisible(sailIndx);

              gridApi.setFocusedCell(sailIndx, "sailNum");
              gridApi.startEditingCell({
                rowIndex: sailIndx,
                colKey: "sailNum",
              });
            }
          }, 100);

          return;
        }

        if (
          event.newValue &&
          (aboutFinishRaceEntrants.some(
            (x) => x.sailNum.toLowerCase() === event.newValue.toLowerCase()
          ) ||
            completeFinishRaceEntrants.filter(
              (x) =>
                x.sailNum?.trim().toLowerCase() ===
                event.newValue.trim().toLowerCase()
            ).length >= 2)
        ) {
          setTimeout(() => {
            props.activateSnackbarTab(
              `Sail Number ${event.newValue} already added!`,
              "error",
              true
            );
          }, 300);
          event.data.sailNum = event.oldValue ? event.oldValue : "";
          const sailIndx = event.node.rowIndex;
          console.log("THE INDX", sailIndx);

          // Refocus the cell
          setTimeout(() => {
            const gridApi = gridRefs[2]?.current?.api;
            if (gridApi && sailIndx !== -1) {
              gridApi.ensureIndexVisible(sailIndx);

              gridApi.setFocusedCell(sailIndx, "sailNum");
              gridApi.startEditingCell({
                rowIndex: sailIndx,
                colKey: "sailNum",
              });
            }
          }, 100);

          return;
        }

        let oldRow = props.allUnfinished.filter(
          (x) => x?.sailNum?.toLowerCase() == event?.oldValue?.toLowerCase()
        );
        console.log("OLD---", oldRow);

        let unFinishRow = snWithAlpha
          ? unfinishedEntrants.filter(
              (x) =>
                x?.sailNum?.toLowerCase() === event?.newValue?.toLowerCase()
            )
          : unfinishedEntrants.filter(
              (x) =>
                x?.sailNum?.toLowerCase().replace(/[A-Za-z]/g, "") ===
                event?.newValue?.toLowerCase()
            );
        if (unFinishRow.length > 0) {
          let unfinishedEntrants2 = snWithAlpha
            ? unfinishedEntrants.filter(
                (x) =>
                  x?.sailNum?.toLowerCase() !== event?.newValue?.toLowerCase()
              )
            : unfinishedEntrants.filter(
                (x) =>
                  x?.sailNum?.toLowerCase().replace(/[A-Za-z]/g, "") !==
                  event?.newValue?.toLowerCase()
              );

          let completeFinishRaceEntrants2 = completeFinishRaceEntrants.filter(
            (x) => x.id !== event.data.id
          );

          console.log(
            "THE completeFinishRaceEntrants2",
            completeFinishRaceEntrants2
          );
          unFinishRow[0].hr = event.data.hr
            ? String(event.data.hr).padStart(2, "0")
            : "00";
          unFinishRow[0].min = event.data.min
            ? String(event.data.min).padStart(2, "0")
            : "00";
          unFinishRow[0].sec = event.data.sec
            ? String(event.data.sec).padStart(2, "0")
            : "00";
          unFinishRow[0].position = event.data.position;
          unFinishRow[0].place = event.data.place;
          unFinishRow[0].dn = event.data.dn;
          unFinishRow[0].oldSailNum = event.oldValue;
          unFinishRow[0].finishDate = props.overnight
            ? event.data.finishDate
              ? event.data.finishDate
              : new Date().toISOString().split("T")[0]
            : "";

          completeFinishRaceEntrants2.splice(
            event.node.rowIndex,
            0,
            unFinishRow[0]
          );

          console.log("completeFinishRaceEntrants", completeFinishRaceEntrants);
          unfinishedEntrants = [...unfinishedEntrants2];
          dispatch(actions.updateInitialFinishRaceEntrants(unfinishedEntrants));
          console.log(
            "completeFinishRaceEntrants2",
            completeFinishRaceEntrants2
          );

          completeFinishRaceEntrants = [...completeFinishRaceEntrants2];
          dispatch(
            actions.updateCompleteFinishRaceEntrants(completeFinishRaceEntrants)
          );
          let finishedData = makePayloadForUpdate(
            unFinishRow[0],
            unFinishRow[0].hr,
            unFinishRow[0].min,
            unFinishRow[0].sec,
            event.context.sessionId
          );
          saveRow(finishedData);
        } else {
          event.data.fleetName = "";
          event.data.boatName = "";
          event.data.hr = event.data.hr
            ? String(event.data.hr).padStart(2, "0")
            : "00";
          event.data.min = event.data.min
            ? String(event.data.min).padStart(2, "0")
            : "00";
          event.data.sec = event.data.sec
            ? String(event.data.sec).padStart(2, "0")
            : "00";
          event.data.id = "";
          event.data.editable = true;
          event.data.oldSailNum = event.oldValue;

          let finishedRow = completeFinishRaceEntrants.filter(
            (x) => x?.sailNum?.toLowerCase() === event?.newValue?.toLowerCase()
          );
          console.log("the finished", finishedRow);
          console.log("the event data", event.data);
          if (finishedRow) {
            let updatedCompleteFinishRaceEntrants =
              completeFinishRaceEntrants.map((entrant) =>
                entrant?.sailNum?.toLowerCase() ===
                event?.newValue?.toLowerCase()
                  ? event.data
                  : entrant
              );
            console.log("the success row", updatedCompleteFinishRaceEntrants);
            dispatch(
              actions.updateCompleteFinishRaceEntrants(
                updatedCompleteFinishRaceEntrants
              )
            );
            let finishedData = makePayloadForUpdate(
              event.data,
              event.data.hr,
              event.data.min,
              event.data.sec,
              event.context.sessionId
            );
            saveRow(finishedData);
          }
        }

        if (oldRow.length > 0) {
          unfinishedEntrants = [...unfinishedEntrants, ...oldRow];
          console.log("INSIDE---", unfinishedEntrants);
          dispatch(actions.updateInitialFinishRaceEntrants(unfinishedEntrants));
        }

        dispatch(
          actions.updateFinishRaceManager([
            ...unFinishRow,
            ...aboutFinishRaceEntrants,
            ...completeFinishRaceEntrants,
          ])
        );
      },
    },
    {
      field: "hr",
      flex: 1,
      minWidth: 50,
      maxWidth: 80,
      //sortable: true,
      headerName: "HR",
      editable: (params) => !params.data?.isAbandon,
      cellEditor: "agTextCellEditor",
      cellEditorParams: {
        maxLength: 2,
      },
      valueSetter: (params) => {
        const newValue = params.newValue;

        // Only allow numeric values and empty string
        if (/^\d*$/.test(newValue) && newValue.length <= 2) {
          const numericValue = parseInt(newValue, 10);

          // Validate range (0-24)
          if (newValue === "" || (numericValue >= 0 && numericValue < 24)) {
            params.data.hr =
              newValue === "" ? "" : String(numericValue).padStart(2, "0");
            params.data.errorHR = false;
            params.data.dn =
              params.data.dn && !timePenalties.includes(params.data.dn)
                ? ""
                : params.data.dn;

            // Auto-save logic
            params.data.oldSailNum = "";
            let data = makePayloadForUpdate(
              params.data,
              params.data.hr ? String(params.data.hr).padStart(2, "0") : "00",
              params.data.min ? String(params.data.min).padStart(2, "0") : "00",
              params.data.sec ? String(params.data.sec).padStart(2, "0") : "00",
              params.context.sessionId
            );
            saveRow(data, params.context);

            return true;
          }
        }

        params.data.errorHR = true;
        return false;
      },
      cellClassRules: {
        error: (params) => params.data.errorHR,
      },
      onCellValueChanged: (params) => {
        if (params.newValue && params.newValue.length === 1) {
          params.node.setDataValue("hr", params.newValue.padStart(2, "0"));
        }
      },
    },
    {
      field: "min",
      headerName: "MIN",
      flex: 1,
      minWidth: 50,
      maxWidth: 80,
      //sortable: true,
      editable: (params) => !params.data?.isAbandon,
      cellEditor: "agTextCellEditor",
      cellEditorParams: {
        maxLength: 2,
      },
      valueSetter: (params) => {
        const newValue = params.newValue;

        // Only allow numeric values and empty string
        if (/^\d*$/.test(newValue) && newValue.length <= 2) {
          const numericValue = parseInt(newValue, 10);

          if (newValue === "" || (numericValue >= 0 && numericValue < 60)) {
            params.data.min =
              newValue === "" ? "" : String(numericValue).padStart(2, "0");
            params.data.errorMin = false;
            params.data.dn =
              params.data.dn && !timePenalties.includes(params.data.dn)
                ? ""
                : params.data.dn;

            params.data.oldSailNum = "";
            let data = makePayloadForUpdate(
              params.data,
              params.data.hr ? String(params.data.hr).padStart(2, "0") : "00",
              params.data.min ? String(params.data.min).padStart(2, "0") : "00",
              params.data.sec ? String(params.data.sec).padStart(2, "0") : "00",
              params.context.sessionId
            );
            saveRow(data, params.context);

            return true;
          }
        }

        params.data.errorMin = true;
        return false;
      },
      cellClassRules: {
        error: (params) => params.data.errorMin,
      },
      onCellValueChanged: (params) => {
        if (params.newValue && params.newValue.length === 1) {
          params.node.setDataValue("min", params.newValue.padStart(2, "0"));
        }
      },
    },
    {
      field: "sec",
      headerName: "SEC",
      editable: (params) => !params.data?.isAbandon,
      flex: 1,
      //sortable: true,
      minWidth: 60,
      maxWidth: 100,
      cellEditor: "agTextCellEditor",
      cellEditorParams: {
        maxLength: 2,
      },
      valueSetter: (params) => {
        const newValue = params.newValue;

        // Only allow numeric values and empty string
        if (/^\d*$/.test(newValue) && newValue.length <= 2) {
          const numericValue = parseInt(newValue, 10);

          if (newValue === "" || (numericValue >= 0 && numericValue < 60)) {
            params.data.sec =
              newValue === "" ? "" : String(numericValue).padStart(2, "0");
            params.data.errorSec = false;
            params.data.dn =
              params.data.dn && !timePenalties.includes(params.data.dn)
                ? ""
                : params.data.dn;

            // Auto-save logic
            params.data.oldSailNum = "";
            let data = makePayloadForUpdate(
              params.data,
              params.data.hr ? String(params.data.hr).padStart(2, "0") : "00",
              params.data.min ? String(params.data.min).padStart(2, "0") : "00",
              params.data.sec ? String(params.data.sec).padStart(2, "0") : "00",
              params.context.sessionId
            );
            saveRow(data, params.context);

            return true;
          }
        }

        params.data.errorSec = true;
        return false;
      },
      cellClassRules: {
        error: (params) => params.data.errorSec,
      },
      onCellValueChanged: (params) => {
        if (params.newValue && params.newValue.length === 1) {
          params.node.setDataValue("sec", params.newValue.padStart(2, "0"));
        }
      },
    },
    {
      field: "dn",
      headerName: "DN",
      suppressKeyboardEvent: (params) => {
        return ["ArrowDown", "ArrowUp", "Enter"].includes(params.event.key);
      },
      flex: 2,
      minWidth: 100,
      maxWidth: 160,
      editable: (params) => !params.data?.isAbandon,
      //sortable: true,
      cellEditor: PenaltySelectEditor,
      cellEditorParams: { params: props.penalties },
      valueFormatter: (params) =>
        params.data?.isAbandon ? "ABN" : params.value,
      valueSetter: (params) => {
        const selectedValue = params.newValue;

        if (selectedValue === "") {
          params.data.dn = "";
        } else {
          params.data.dn = selectedValue;
        }

        if (!timePenalties.includes(selectedValue) && params?.data?.dn != "") {
          console.log("Selected Value:", selectedValue);
          params.data.hr = "00";
          params.data.min = "00";
          params.data.sec = "00";
          params.data.errorHR = false;
          params.data.errorMin = false;
          params.data.errorSec = false;
        }
        if (
          selectedValue !== "" &&
          (selectedValue === "DNC" || !timePenalties.includes(selectedValue))
        ) {
          params.data.finishDate = "";
        }
        params.data.oldSailNum = "";
        let data = makePayloadForUpdate(
          params.data,
          params.data.hr ? String(params.data.hr).padStart(2, "0") : "00",
          params.data.min ? String(params.data.min).padStart(2, "0") : "00",
          params.data.sec ? String(params.data.sec).padStart(2, "0") : "00",
          params.context.sessionId
        );
        saveRow(data, params.context);
        //setSaveData(true);
        return true; // Ensure AG Grid updates the value
      },
    },
    {
      field: "finishDate",
      headerName: "Date",
      hide: !props.overnight,
      flex: 2,
      minWidth: 150,
      maxWidth: 200,
      filter: "agDateColumnFilter", // Enables date filtering
      // editable: params => !params.data?.isAbandon, // Makes the column editable
      editable: (params) =>
        !params.data?.isAbandon &&
        !(
          params.data?.dn &&
          (params.data.dn === "DNC" || !timePenalties.includes(params.data.dn))
        ),

      cellEditor: "agDateCellEditor", // Uses the built-in AG Grid Date Picker
      // cellEditorParams: {
      //   params: { input: { type: "date" } },
      // },
      cellEditorParams: {
        useBrowserDatePicker: true,
      },

      // Display properly formatted date
      // valueFormatter: (params) => {
      //   if (!params.value) return "";
      //   return new Date(params.value).toLocaleDateString(systemLocale);
      // },

      valueFormatter: (params) => {
        if (!params.value) return "";

        if (
          params.data?.dn &&
          (params.data.dn === "DNC" || !timePenalties.includes(params.data.dn))
        ) {
          return "";
        }

        return new Date(params.value).toLocaleDateString(systemLocale);
      },

      // valueGetter: (params) => {
      //   // Show blank if DNC or no finishDate, otherwise show the stored value
      //   if (
      //     !params.data?.finishDate ||
      //     (params.data.dn != "" &&
      //       (params.data.dn === "DNC" ||
      //         !timePenalties.includes(params.data.dn)))
      //   )
      //     return "";
      //   return params.data.finishDate;
      // },
      valueGetter: (params) => {
        if (!params.data?.finishDate) return null;
        return new Date(params.data.finishDate);
      },

      // Handle update
      // valueSetter: (params) => {
      //   if (!params.newValue) return false;

      //   const date = new Date(params.newValue);
      //   const localDate = new Date(
      //     date.getTime() - date.getTimezoneOffset() * 60000
      //   );
      //   const formatted = localDate.toISOString().split("T")[0];

      //   // update real field
      //   params.data.finishDate = formatted;

      //   // custom update logic
      //   params.data.oldSailNum = "";
      //   const data = makePayloadForUpdate(
      //     params.data,
      //     params.data.hr ? String(params.data.hr).padStart(2, "0") : "00",
      //     params.data.min ? String(params.data.min).padStart(2, "0") : "00",
      //     params.data.sec ? String(params.data.sec).padStart(2, "0") : "00",
      //     params.context.sessionId
      //   );

      //   saveRow(data, params.context);

      //   return true;
      // },
      valueSetter: (params) => {
        if (
          params.data?.dn &&
          (params.data.dn === "DNC" || !timePenalties.includes(params.data.dn))
        ) {
          return false;
        }

        // Handle clearing the date
        if (!params.newValue || params.newValue === "") {
          params.data.finishDate = "";
          
          saveRow(
            makePayloadForUpdate(
              params.data,
              params.data.hr ? String(params.data.hr).padStart(2, "0") : "00",
              params.data.min ? String(params.data.min).padStart(2, "0") : "00",
              params.data.sec ? String(params.data.sec).padStart(2, "0") : "00",
              params.context.sessionId
            ),
            params.context
          );
          
          return true;
        }

        if (!(params.newValue instanceof Date)) return false;

        const localDate = new Date(
          params.newValue.getTime() -
            params.newValue.getTimezoneOffset() * 60000
        );

        params.data.finishDate = localDate.toISOString().split("T")[0];

        saveRow(
          makePayloadForUpdate(
            params.data,
            params.data.hr ? String(params.data.hr).padStart(2, "0") : "00",
            params.data.min ? String(params.data.min).padStart(2, "0") : "00",
            params.data.sec ? String(params.data.sec).padStart(2, "0") : "00",
            params.context.sessionId
          ),
          params.context
        );

        return true;
      },
    },
    {
      field: "fleetName",
      headerName: "FLEET",
      flex: 2,
      minWidth: 80,
      maxWidth: 100,
      //sortable: true,
      cellRenderer: (params) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            width: "100%",
          }}
        >
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              width: "100%",
            }}
            title={params.value || ''}
          >
            {params.value}
          </span>
        </div>
      ),
    },
    {
      field: "del",
      headerName: "DROP",
      flex: 2,
      //sortable: true,
      minWidth: 100,
      maxWidth: 180,
      cellRenderer: (params) => (
        <Button
          color="primary"
          className="ty_cross_btn"
          onClick={() =>
            handleCompletedDelClick(params.data.sailNum, params.data, params)
          }
          disabled={params.data?.isAbandon}
        >
          ×
        </Button>
      ),
    },
  ];

  const saveRow = async (data, contxt = {}) => {
    // setLoading(true)
    console.log("The context value", Object.keys(contxt).length);
    unfinishedEntrants =
      Object.keys(contxt).length > 0
        ? contxt.unfinishedEntrants
        : unfinishedEntrants;
    aboutFinishRaceEntrants =
      Object.keys(contxt).length > 0
        ? contxt.aboutFinishRaceEntrants
        : aboutFinishRaceEntrants;
    completeFinishRaceEntrants =
      Object.keys(contxt).length > 0
        ? contxt.completeFinishRaceEntrants
        : completeFinishRaceEntrants;

    const UPDATE_FINISH_TIMES = `api/clubs/FinishTime`;
    console.log("the Payload", data);

    let response = "";
    try {
      response = await axiosInstance.post(UPDATE_FINISH_TIMES, data);

      // Handle response
      console.log("Response:", response.data);
      if (response.data.success) {
        const updatedCompleteFinishRaceEntrants =
          completeFinishRaceEntrants.map((entrant) =>
            entrant.sailNum === data.sailNum
              ? { ...entrant, editable: response?.data?.data?.editable }
              : entrant
          );

        // Only dispatch if something was actually updated
        const hasUpdate = completeFinishRaceEntrants.some(
          (entrant) => entrant.sailNum === data.sailNum
        );

        if (hasUpdate) {
          dispatch(
            actions.updateCompleteFinishRaceEntrants(
              updatedCompleteFinishRaceEntrants
            )
          );
        }
      }
    } catch (error) {
      console.log("the data 123", data);
      let finishedRow = completeFinishRaceEntrants.filter(
        (x) => x.sailNum === data.sailNum
      );
      console.log("the error finishedRow", finishedRow);
      if (finishedRow) {
        let updatedCompleteFinishRaceEntrants = completeFinishRaceEntrants.map(
          (entrant) =>
            entrant.sailNum === data.sailNum
              ? { ...entrant, editable: true }
              : entrant
        );
        console.log("the error row", updatedCompleteFinishRaceEntrants);
        dispatch(
          actions.updateCompleteFinishRaceEntrants(
            updatedCompleteFinishRaceEntrants
          )
        );
      }
      props.activateSnackbarTab(error.response.data.message, "error", true);
      console.error("Error updating finish times:", error);
      //  }
    } finally {
      // setSaveLoader(false);
      // setSaveData(false);
      //setLoading(false)
      setPayload([]);
    }
  };

  const savePrefinished = async () => {
    if (props.session != "" && props.session > 0) {
      let response = "";
      try {
        let prefinishData = {};
        prefinishData["finishTimeDTOs0"] = [];
        prefinishData["finishTimeDTOs1"] = aboutFinishRaceEntrants;
        prefinishData["finishTimeDTOs2"] = [];
        const encryptedSessionId = encodeURIComponent(
          encrypt(JSON.stringify(props.session))
        );
        const UPDATE_FINISH_TIMES =
          "api/clubs/FinishTime/prefinish?raceSession=" + encryptedSessionId;
        response = await axiosInstance.post(UPDATE_FINISH_TIMES, prefinishData);
        console.log("The prefinished response", response);
      } catch (error) {
        console.log("The prefinished error", error);
        if (!error.response.data.success) {
        }
      }
    }
  };

  const deleteRow = async (data) => {
    const UPDATE_FINISH_TIMES = `api/clubs/FinishTime/delete`;
    console.log("the finishTimePayload", data);
    try {
      const response = await axiosInstance.post(UPDATE_FINISH_TIMES, data);
      /*  */
      // Handle response
      console.log("Response:", response.data);
    } catch (error) {
      // Handle errors
      console.error("Error updating finish times:", error);
    } finally {
      // setSaveLoader(false);
      setPayload([]);
      // setDltData(false);
    }
  };

  const handleBoxClick = (params) => {
    //   setLoading(true)
    console.log("Box clicked!", params);
    console.log("Box clicked aboutFinishRaceEntrants", aboutFinishRaceEntrants);
    unfinishedEntrants = params.context.unfinishedEntrants;
    aboutFinishRaceEntrants = params.context.aboutFinishRaceEntrants;
    completeFinishRaceEntrants = params.context.completeFinishRaceEntrants;

    if (
      params.value &&
      (completeFinishRaceEntrants.some(
        (x) => x.sailNum.toLowerCase() === params?.value?.toLowerCase()
      ) ||
        aboutFinishRaceEntrants.filter(
          (x) =>
            x.sailNum?.trim().toLowerCase() ===
            params?.value?.trim().toLowerCase()
        ).length >= 1)
    ) {
      props.activateSnackbarTab(
        `Sail Number ${params.value} already added in about to finish!`,
        "error",
        true
      );
      return;
    }

    let unFinishRow = unfinishedEntrants.filter(
      (x) => x.sailNum.toLowerCase() == params.value.toLowerCase()
    );
    if (unFinishRow.length > 0) {
      let unfinishedEntrants2 = unfinishedEntrants.filter(
        (x) => x.sailNum.toLowerCase() !== params.value.toLowerCase()
      );
      console.log(
        "the completeFinishRaceEntrants com",
        completeFinishRaceEntrants
      );

      let hasBlankSailNum = aboutFinishRaceEntrants.some(
        (entrant) => !entrant.sailNum || entrant.sailNum.trim() === ""
      );
      console.log("the blanck", hasBlankSailNum);
      let aboutFinishRaceEntrants2 = [];
      console.log("the aboutFinishRaceEntrants2 com", aboutFinishRaceEntrants2);
      if (aboutFinishRaceEntrants.length >= 1 && hasBlankSailNum) {
        let firstBlankFound = false;

        let updatedEntrants = aboutFinishRaceEntrants.map((entrant) => {
          if (
            !firstBlankFound &&
            (!entrant.sailNum || entrant.sailNum.trim() === "")
          ) {
            firstBlankFound = true;
            return { ...unFinishRow[0] };
          }
          return entrant; // Keep others unchanged
        });
        aboutFinishRaceEntrants2 = [...updatedEntrants];
      } else {
        aboutFinishRaceEntrants.push(unFinishRow[0]);
        aboutFinishRaceEntrants2 = [...aboutFinishRaceEntrants];
      }
      console.log("aboutFinishRaceEntrants", aboutFinishRaceEntrants);
      unfinishedEntrants = [...unfinishedEntrants2];
      dispatch(actions.updateInitialFinishRaceEntrants(unfinishedEntrants));

      dispatch(actions.updateAboutFinishRaceEntrants(aboutFinishRaceEntrants2));
    }

    setTimeout(() => {
      if (gridRefs[1].current && gridRefs[1].current.api) {
        const rowIndex = aboutFinishRaceEntrants.length - 1; // Last row index
        gridRefs[1].current.api.ensureIndexVisible(rowIndex);

        gridRefs[1].current.api.setFocusedCell(rowIndex, "sailNum"); // Focus on the "sailNum" column
        gridRefs[1].current.api.startEditingCell({
          rowIndex:
            aboutFinishRaceEntrants.length > 0
              ? aboutFinishRaceEntrants.length - 1
              : 0, // Index of new row
          colKey: "sailNum", // Column to focus on
        });
      }
      //   setLoading(false)
    }, 100);
  };

  const handleCompletedDelClick = async (cellvalue, celldata, params) => {
    console.log("Now  unfinishedEntrants clicked!", celldata);

    console.log("Button clicked for delete:", params);

    // params.data.buttonDisabled = false;

    //console.log("Time ..................." + formatTime());
    setSortField("position");
    setIsSortAsc(false);
    unfinishedEntrants = params.context.unfinishedEntrants;
    aboutFinishRaceEntrants = params.context.aboutFinishRaceEntrants;
    completeFinishRaceEntrants = params.context.completeFinishRaceEntrants;

    //alert(`Clicked on row:, value: ${cellvalue}`);
    /*  setPayload(
      makePayloadForUpdate(celldata, celldata.hr, celldata.min, celldata.sec)
    ); */
    console.log("the payload", payload);
    //  setFinishTimePayload([payload]);

    let unFinishRow = completeFinishRaceEntrants.filter(
      (x) => x.id == celldata.id
    ); //completeFinishRaceEntrants
    if (unFinishRow.length > 0) {
      let completeFinishRaceEntrants2 = completeFinishRaceEntrants.filter(
        // (x) => x.id != celldata.id
        (x) => x.sailNum.toLowerCase() != celldata.sailNum.toLowerCase()
      ); //completeFinishRaceEntrants2

      let unfinishedEntrants2 = [...unfinishedEntrants];
      if (unFinishRow[0].boatName) {
        unFinishRow[0].hr = "00";
        unFinishRow[0].min = "00";
        unFinishRow[0].sec = "00";
        unFinishRow[0].dn = "";
        unFinishRow[0].place = "";
        unFinishRow[0].position = "";
        unFinishRow[0].editable = false;
        unFinishRow[0].errorHR = false;
        unFinishRow[0].errorMin = false;
        unFinishRow[0].errorSec = false;
        unfinishedEntrants2.push(unFinishRow[0]);
        unfinishedEntrants = [...unfinishedEntrants2];
        dispatch(actions.updateInitialFinishRaceEntrants(unfinishedEntrants));
      }

      completeFinishRaceEntrants2.sort((a, b) => b.position - a.position);

      let updated = completeFinishRaceEntrants2.map((entrant, index) => ({
        ...entrant,
        position: completeFinishRaceEntrants2.length - index,
      }));

      completeFinishRaceEntrants = [...updated]; //completeFinishRaceEntrants2

      dispatch(
        actions.updateCompleteFinishRaceEntrants(completeFinishRaceEntrants)
      );
      let finishedData = makePayloadForUpdate(
        celldata,
        celldata.hr,
        celldata.min,
        celldata.sec,
        params.context.sessionId
      );
      deleteRow(finishedData);
    }
  };

  const handleNowClick = async (
    cellvalue,
    celldata,
    params,
    fromUnfinished
  ) => {
    // setLoading(true)

    /*  if (params.data.buttonDisabled && fromUnfinished) return;

    if(fromUnfinished){
      // Example: disable the button
      params.data.buttonDisabled = true;

      // Refresh this row to re-render button
      params.api.refreshCells({
        rowNodes: [params.node],
        columns: ['capture'],
        force: true,
      });

       // Your logic here (e.g., API call)
      console.log("Button clicked for:", params);
    } */

    console.log("Now  unfinishedEntrants clicked!", celldata);
    console.log("Now  cell value!", cellvalue);
    console.log("Now  params clicked!", params.data.hr);
    console.log("THE SESSION ID", params.context.sessionId);
    //  console.log("Time ..................." + formatTime());

    cellvalue = cellvalue === null ? "" : cellvalue;

    unfinishedEntrants = params.context.unfinishedEntrants;
    aboutFinishRaceEntrants = params.context.aboutFinishRaceEntrants;
    completeFinishRaceEntrants = params.context.completeFinishRaceEntrants;

    // Get the correct row index
    const currentRowIndex = aboutFinishRaceEntrants.findIndex(
      (x) => x.id === celldata.id
    );

    console.log("THE INDEX:", currentRowIndex);

    if (
      cellvalue &&
      (completeFinishRaceEntrants.some(
        (x) => x.sailNum.toLowerCase() === cellvalue.toLowerCase()
      ) ||
        aboutFinishRaceEntrants.filter(
          (x) =>
            x.sailNum?.trim().toLowerCase() === cellvalue.trim().toLowerCase()
        ).length >= 2)
    ) {
      props.activateSnackbarTab(
        `Sail Number ${cellvalue} already added!`,
        "error",
        true
      );

      setTimeout(() => {
        if (gridRefs[1].current && gridRefs[1].current.api) {
          // Ensure the correct row is visible
          gridRefs[1].current.api.ensureIndexVisible(currentRowIndex);

          // Focus on the correct row and column
          gridRefs[1].current.api.setFocusedCell(currentRowIndex, "sailNum");

          gridRefs[1].current.api.startEditingCell({
            rowIndex: currentRowIndex, // Use the correct row index
            colKey: "sailNum",
          });
        }
      }, 100);

      return;
    }

    if (cellvalue === "") {
      props.activateSnackbarTab("Please define sail number", "error", true);
      setTimeout(() => {
        if (gridRefs[1].current && gridRefs[1].current.api) {
          const rowIndex = currentRowIndex; // Last row index
          gridRefs[1].current.api.ensureIndexVisible(rowIndex);

          gridRefs[1].current.api.setFocusedCell(rowIndex, "sailNum"); // Focus on the "sailNum" column
          gridRefs[1].current.api.startEditingCell({
            rowIndex:
              aboutFinishRaceEntrants.length > 0
                ? aboutFinishRaceEntrants.length - 1
                : 0, // Index of new row
            colKey: "sailNum", // Column to focus on
          });
        }
      }, 100);
      return;
    }

    let tempTime = new Date();
    let hourValue = tempTime.getHours().toString().padStart(2, "0");
    let minValue = tempTime.getMinutes().toString().padStart(2, "0");
    let secValue = tempTime.getSeconds().toString().padStart(2, "0");

    console.log(hourValue, minValue, secValue); // Example output: "09" "05" "03"

    let unFinishRow = snWithAlpha
      ? unfinishedEntrants.filter(
          (x) => x.sailNum.toLowerCase() === cellvalue.toLowerCase()
        )
      : unfinishedEntrants.filter(
          (x) =>
            x.sailNum.toLowerCase().replace(/[A-Za-z]/g, "") ===
            cellvalue.toLowerCase()
        );
    let aboutFinRow =
      cellvalue === ""
        ? aboutFinishRaceEntrants.filter((x) => x.id === celldata?.id || -1)
        : aboutFinishRaceEntrants.filter(
            (x) => x.sailNum.toLowerCase() === cellvalue.toLowerCase()
          );
    let completeFinishRaceEntrants2 = [...completeFinishRaceEntrants];
    console.log("the fetched entrants", unFinishRow);
    if (unFinishRow.length > 0) {
      let unfinishedEntrants2;

      if (snWithAlpha) {
        unfinishedEntrants2 = unfinishedEntrants.filter(
          (x) => x.sailNum.toLowerCase() !== cellvalue.toLowerCase()
        );
      } else {
        unfinishedEntrants2 = unfinishedEntrants.filter(
          (x) =>
            x.sailNum.toLowerCase().replace(/[A-Za-z]/g, "") !==
            cellvalue.toLowerCase()
        );
      }
      console.log("THE unfinishedEntrants2", unfinishedEntrants2);
      let aboutFinishRaceEntrants2 = aboutFinishRaceEntrants.filter(
        (x) => x.id !== params.data.id
      );
      if (!fromUnfinished) {
        const combinedArray = [
          ...unfinishedEntrants,
          ...aboutFinishRaceEntrants,
          ...completeFinishRaceEntrants,
        ];
        const maxId = combinedArray.reduce(
          (max, item) => (item.id > max ? item.id : max),
          0
        );
        if (aboutFinishRaceEntrants2.length < 6) {
          aboutFinishRaceEntrants2.push({
            id: maxId + 1,
            hr: "00",
            min: "00",
            sec: "00",
            dn: "",
            fleetName: "",
            boatName: "",
            sailNum: "",
            editable: true,
          });
        }
      }

      unFinishRow[0].hr = hourValue;
      unFinishRow[0].min = minValue;
      unFinishRow[0].sec = secValue;
      unFinishRow[0].position = completeFinishRaceEntrants.length + 1;
      unFinishRow[0].place = completeFinishRaceEntrants.length + 1;
      unFinishRow[0].finishDate = props.overnight
        ? new Date().toISOString().split("T")[0]
        : "";
      unFinishRow[0].oldSailNum = "";

      completeFinishRaceEntrants2.unshift(unFinishRow[0]);

      completeFinishRaceEntrants = [...completeFinishRaceEntrants2];

      unfinishedEntrants = [...unfinishedEntrants2];

      dispatch(actions.updateInitialFinishRaceEntrants(unfinishedEntrants));
      dispatch(actions.updateAboutFinishRaceEntrants(aboutFinishRaceEntrants2));
      dispatch(
        actions.updateCompleteFinishRaceEntrants(completeFinishRaceEntrants)
      );
      let finishedData = makePayloadForUpdate(
        unFinishRow[0],
        hourValue,
        minValue,
        secValue,
        params.context.sessionId
      );
      saveRow(finishedData);
      //  setPayload(f(unFinishRow[0], hourValue, minValue, secValue));
    } else if (aboutFinRow.length > 0) {
      aboutFinRow[0].hr = hourValue;
      aboutFinRow[0].min = minValue;
      aboutFinRow[0].sec = secValue;
      aboutFinRow[0].position = completeFinishRaceEntrants.length + 1;
      aboutFinRow[0].place = completeFinishRaceEntrants.length + 1;
      aboutFinRow[0].sailNum = aboutFinRow[0].sailNum.toUpperCase();
      aboutFinRow[0].finishDate = props.overnight
        ? new Date().toISOString().split("T")[0]
        : "";
      completeFinishRaceEntrants2.unshift(aboutFinRow[0]);

      completeFinishRaceEntrants = [...completeFinishRaceEntrants2];

      console.log("the new about 1", aboutFinishRaceEntrants);
      let aboutFinishRaceEntrants2 =
        cellvalue === ""
          ? aboutFinishRaceEntrants.filter((x) => x.id !== celldata.id)
          : aboutFinishRaceEntrants.filter(
              (x) => x.sailNum.toUpperCase() !== cellvalue.toUpperCase()
            );
      console.log("the new about 2", aboutFinishRaceEntrants2);
      if (!fromUnfinished) {
        const combinedArray = [
          ...unfinishedEntrants,
          ...aboutFinishRaceEntrants,
          ...completeFinishRaceEntrants,
        ];
        const maxId = combinedArray.reduce(
          (max, item) => (item.id > max ? item.id : max),
          0
        );
        if (aboutFinishRaceEntrants2.length < 6) {
          aboutFinishRaceEntrants2.push({
            id: maxId + 1,
            hr: "00",
            min: "00",
            sec: "00",
            dn: "",
            fleetName: "",
            boatName: "",
            sailNum: "",
            editable: true,
          });
        }
      }
      console.log("the new about", aboutFinishRaceEntrants2);
      dispatch(actions.updateAboutFinishRaceEntrants(aboutFinishRaceEntrants2));
      dispatch(
        actions.updateCompleteFinishRaceEntrants(completeFinishRaceEntrants)
      );
      let finishedData = makePayloadForUpdate(
        aboutFinRow[0],
        hourValue,
        minValue,
        secValue,
        params.context.sessionId
      );
      saveRow(finishedData);
    }
  };

  const makePayloadForUpdate = (obj, hrValue, minValue, secValue, id) => {
    console.log("THE OBJ", obj);
    console.log("THE OBJ session id", id);

    let targetObj = {};
    const isCapturedNow = obj.editable;
    targetObj["raceSessionId"] = id;
    targetObj["sessionEntrantId"] = isCapturedNow ? "" : obj.sessionEntrantId;
    console.log("THE isCapturedNow", isCapturedNow);
    if (obj.status !== undefined) {
      targetObj["sessionEntrantId"] = obj.sessionEntrantId
        ? obj.sessionEntrantId
        : "";
    } else {
      targetObj["sessionEntrantId"] = isCapturedNow ? "" : obj.id;
    }

    targetObj["place"] = obj.position;
    targetObj["sailNum"] = obj.sailNum;

    // Ensure hr, min, sec are properly fetched and formatted
    targetObj["hr"] = hrValue;
    targetObj["mm"] = minValue;
    targetObj["ss"] = secValue;

    targetObj["date"] = props.raceSessionsDate;
    targetObj["finishDate"] = obj.finishDate;
    targetObj["dn"] = obj.dn;
    targetObj["fleet"] = obj.fleetName;
    targetObj["oldSailNum"] = obj.oldSailNum ? obj.oldSailNum : null;
    if (props?.caseOTBEntrant) {
      targetObj["class"] = obj?.class ? obj?.class : "";
    }
    console.log("the oldSailNum", obj.oldSailNum);
    console.log("Final Payload:", targetObj);
    return targetObj;
  };

  const mapRowToPayloadElement = (obj) => {
    let targetObj = {};
    targetObj["raceSessionId"] = props.session;
    if (obj.status != undefined) {
      targetObj["sessionEntrantId"] = obj.sessionEntrantId;
    } else {
      targetObj["sessionEntrantId"] = obj.id;
    }
    targetObj["place"] = 0;
    targetObj["sailNum"] = obj.sailNum;
    targetObj["hr"] = "00";
    targetObj["mm"] = "00";
    targetObj["ss"] = "00";
    targetObj["date"] = new Date()
      .toLocaleString("en-GB", { timeZone: timeZone })
      .split(",")[0]
      .trim()
      .split("/")
      .reverse()
      .join("-");
    targetObj["dn"] = "DNC";
    targetObj["fleet"] = obj.fleetName;
    return targetObj;
  };

  const getFinishTimeCalculations = async () => {
    try {
      console.log("THE FINISHeD ---- ", completeFinishRaceEntrants);
      if (
        completeFinishRaceEntrants.some(
          (entrant) => entrant.editable === true || entrant.editable === 1
        )
      ) {
        // Return error or show a message
        console.error("Error: Some entries are still editable.");
        props.activateSnackbarTab(
          "The highlighted finish times are invalid or missing. Please review and correct all finish times before processing results. Results cannot be processed until all times are valid.",
          "error",
          true
        );
        return;
      }

      props.setRefetchRequest(true);
      props.setProcessFlag(true);
      let payloadFirst = unfinishedEntrants
        .concat(aboutFinishRaceEntrants)
        .filter((evt) => !evt.editable)
        .map(mapRowToPayloadElement);
      const CALCULATE_HANDICAP = "api/HC/CalculateHandicap/" + props.session;
      const response = await axiosInstance.post(
        CALCULATE_HANDICAP,
        payloadFirst
      );
      console.log("RES--", response);
      setPayload(response.data.data);
      // setResultProcessEnabledChange(false);
    } catch (error) {
      setPayload([]);
    }
    setRun(true);
  };

  const [showStartTimeModal, setShowStartTimeModal] = useState(false);

  const handleProcessResultsClick = () => {
    if (
      completeFinishRaceEntrants.some(
        (entrant) => entrant.editable === true || entrant.editable === 1
      )
    ) {
      console.error("Error: Some entries are still editable.");
      props.activateSnackbarTab(
        "The highlighted finish times are invalid or missing. Please review and correct all finish times before processing results. Results cannot be processed until all times are valid.",
        "error",
        true
      );
      return;
    }
    setShowStartTimeModal(true);
  };

  const handleStartTimeModalClose = () => {
    setShowStartTimeModal(false);
  };

  const handleUpdateStartTime = () => {
    setShowStartTimeModal(false);
    if (props.setActiveTab) {
      props.setActiveTab("start");
    }
  };

  const handleProceed = async () => {
    setShowStartTimeModal(false);
    await getFinishTimeCalculations();
  };

  const toggleAlpha = () => {
    setSNWithAlpha((prev) => !prev);
    console.log("Grid REF", gridRefs[0]);
    // Refresh the grid cells to reflect changes in the state
    if (gridRefs[0].current) {
      if (gridRefs[0]?.current?.api) {
        console.log("Grid REF INSIDE", gridRefs[0]);
        gridRefs[0].current.api.refreshCells({ force: true });
      } else {
        console.error("Grid reference or API not available");
      }
    }
  };

  const toggleEntrants = () => {
    setOnWaterEntrants((prev) => !prev);
  };

  const focusNextEditableCell = (api, allColumns, rowIndex) => {
    const nextRowIndex = Math.min(rowIndex, api.getDisplayedRowCount() - 1);
    const nextData = api.getDisplayedRowAtIndex(nextRowIndex)?.data;
    const col = allColumns.find((c) =>
      typeof c.editable === "function"
        ? c.editable({ data: nextData })
        : c.editable
    );
    if (col) {
      api.setFocusedCell(nextRowIndex, col.field);
      api.startEditingCell({ rowIndex: nextRowIndex, colKey: col.field });
    }
  };

  const handleCellKeyPress2 = (event) => {
    const key = event.event.key;
    const api = event.api;
    const rowIndex = event.rowIndex;
    const rowData = api.getDisplayedRowAtIndex(rowIndex)?.data;
    const sailNum = rowData?.sailNum;
    const context = event.context;
    const allColumns = api.getColumnDefs().filter((col) => !!col.field);

    const getNextEditableCol = (startIndex) => {
      let index = startIndex;
      while (
        index < allColumns.length &&
        (["boatName", "fleetName"].includes(allColumns[index]?.field) ||
          allColumns[index]?.hide)
      ) {
        index++;
      }
      return allColumns[index];
    };

    const stopAndFocus = (nextRowIndex, nextCol) => {
      const nextData = api.getDisplayedRowAtIndex(nextRowIndex)?.data;
      api.setFocusedCell(nextRowIndex, nextCol.field);

      const isEditable =
        typeof nextCol.editable === "function"
          ? nextCol.editable({ data: nextData })
          : nextCol.editable;

      if (isEditable) {
        // Use a short delay to allow focus to settle before starting edit mode
        setTimeout(() => {
          api.startEditingCell({
            rowIndex: nextRowIndex,
            colKey: nextCol.field,
          });
        }, 100);
      }
    };

    switch (key) {
      case "Tab": {
        event.event.preventDefault();
        api.stopEditing();

        const currentIndex = allColumns.findIndex(
          (c) => c.field === event.column.getColId()
        );
        let nextColIndex = currentIndex + 1;
        let nextCol = getNextEditableCol(nextColIndex);
        let nextRowIndex = rowIndex;

        if (!nextCol) {
          // go to next row's first editable cell
          nextRowIndex++;
          nextCol = getNextEditableCol(0);
        }

        if (nextCol) stopAndFocus(nextRowIndex, nextCol);
        break;
      }

      case "Enter": {
        event.event.preventDefault();
        api.stopEditing();

        if (event.column.getColId() === "del") {
          handleCompletedDelClick(sailNum, rowData, {
            value: sailNum,
            data: rowData,
            context,
          }).then(() =>
            setTimeout(
              () => focusNextEditableCell(api, allColumns, rowIndex),
              0
            )
          );
        }
        break;
      }
      default:
        break;
    }
  };

  function download_csv() {
    console.log("SSS", completeFinishRaceEntrants);
    if (completeFinishRaceEntrants.length === 0) {
      props.activateSnackbarTab(
        "No data in Finished Table to export.",
        "error",
        true
      );
    } else {
      let filteredItems = props.place
        ? completeFinishRaceEntrants.map(
            ({ boatName, sailNum, fleetName, hr, min, sec, place, dn }) => ({
              boatName,
              sailNum,
              fleetName,
              hr,
              min,
              sec,
              place,
              dn,
            })
          )
        : completeFinishRaceEntrants.map(
            ({ boatName, sailNum, fleetName, hr, min, sec, dn }) => ({
              boatName,
              sailNum,
              fleetName,
              hr,
              min,
              sec,
              dn,
            })
          );

      let csvHeader =
        Object.keys(filteredItems[0])
          .map((key) => {
            let header = key.charAt(0).toUpperCase() + key.slice(1);

            if (props.caseOTBEntrant && key === "boatName") {
              header = "Class"; // override if caseOTBEntrant is true
            } else {
              header = header
                .replace("Name", "")
                .replace("SailNum", "Sail No")
                .replace("Hr", "HR")
                .replace("Min", "MN")
                .replace("Sec", "SC")
                .replace("Dn", "DidNot");
            }

            return header;
          })
          .join(",") + "\n";

      console.log("csvHeader", csvHeader);
      let csvBody = filteredItems
        .map((row) => Object.values(row).join(","))
        .join("\n");
      var today = new Date();
      var date =
        today.getFullYear() +
        "-" +
        (today.getMonth() + 1) +
        "-" +
        today.getDate();
      var time =
        today.getHours() + ":" + today.getMinutes() + ":" + today.getSeconds();
      var dateTime = date + "/" + time;

      var hiddenElement = document.createElement("a");
      hiddenElement.href =
        "data:text/csv;charset=utf-8," + encodeURI(csvHeader + csvBody);
      hiddenElement.target = "_blank";
      hiddenElement.download = dateTime + "-RealFinishTime.csv";
      hiddenElement.click();
    }
  }

  const applyFinishedSearch = (value) => {
    if (gridRefs[2]?.current?.api) {
      gridRefs[2].current.api.setFilterModel({
        sailNum: {
          type: "contains",
          filter: value,
        },
      });
    }
  };

  const applyUnfinishedSearch = (value) => {
    setUnfinishedSearchValue(value);
    if (gridRefs[0]?.current) {
      gridRefs[0].current.onFilterChanged();
    }
  };

  const isUnfinishedExternalFilter = () => unfinishedSearchValue.trim() !== "";
  const doesUnfinishedFilterPass = (node) => {
    if (!unfinishedSearchValue.trim()) return true;
    const sailNumMatch = node.data?.sailNum
      ?.toLowerCase()
      .includes(unfinishedSearchValue.toLowerCase());
    const boatNameMatch = node.data?.boatName
      ?.toLowerCase()
      .includes(unfinishedSearchValue.toLowerCase());

    return sailNumMatch || boatNameMatch;
  };

  return (
    <>
      <Grid container>
        <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: {
                lg: 0,
                md: "8px",
                sm: "8px",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: 6,
                alignItems: "center",
                flexDirection: "row",
                pb: 2,
              }}
            >
              <Box>
                <FormLabel className="ty-uppercase-label">
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    ON WATER ENTRANTS
                  </Box>
                </FormLabel>
                <Tooltip
                  title="Turn on this switch to filter only those boats that have been marked 'on water' on TES."
                  placement="top"
                >
                  <FormControlLabel
                    control={
                      <IOSSwitch
                        //disabled={props.isOnWaterDisabled}
                        name={"ON_WATER_ENTRANTS"}
                        checked={onWaterEntrants}
                        onChange={handleOnWaterSwitchChange}
                      />
                    }
                    label={onWaterEntrants ? "Yes" : "No"}
                    labelPlacement="end"
                    sx={{ paddingLeft: "5px", gap: "10px" }}
                  />
                </Tooltip>
              </Box>
              <Box>
                <FormLabel className="ty-uppercase-label">
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    SN WITH ALPHA
                  </Box>
                </FormLabel>
                <FormControlLabel
                  control={
                    <IOSSwitch
                      name="SN_WITH_ALPHA"
                      checked={snWithAlpha}
                      onChange={toggleAlpha}
                    />
                  }
                  label={snWithAlpha ? "Yes" : "No"}
                  labelPlacement="end"
                  sx={{ paddingLeft: "5px", gap: "10px" }}
                />
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
                justifyContent: "end",
                flexWrap: {
                  xl: "nowrap",
                  lg: "nowrap",
                  md: "nowrap",
                  sm: "nowrap",
                  xs: "wrap",
                },
                width: {
                  lg: "auto",
                  md: "100%",
                  sm: "100%",
                },
              }}
            >
              <Button
                className="ty_secondary_btn"
                sx={{ textAlign: "center" }}
                component={Link}
                to={raceSessionURL}
              >
                Return To Race Session
              </Button>
              <Button
                onClick={() => handleProcessResultsClick()}
                className="ty_secondary_btn"
                disabled={props.numEntrants <= 0}
              >
                Process Results
              </Button>

              <Button
                className="ty_secondary_btn"
                onClick={download_csv}
                disabled={props.numEntrants <= 0}
              >
                Export
              </Button>
            </Box>
          </Box>
        </Grid>
        <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
          <Grid container spacing={2}>
            <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
              <Grid1
                gridRefs={gridRefs}
                gridContext={gridContext}
                handleNowClick={handleNowClick}
                // aboutToFinishColumnDefs={aboutToFinishColumnDefs}
                aboutFinishRaceEntrants={aboutFinishRaceEntrants}
                completeFinishRaceEntrants={completeFinishRaceEntrants}
                unfinishedEntrants={unfinishedEntrants}
                //handleCellKeyPress={handleCellKeyPress}
                dispatch={dispatch}
              />
              <Divider
                sx={{
                  borderColor: "#DBDBDB",
                  marginTop: "16px",
                  marginBottom: "12px",
                }}
              />
              <Box>
                <Typography variant="h6" className="ty_form_heading mb-0">
                  FINISHED
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                    flexWrap: "wrap",
                    // gap: 2, // adds spacing between items
                  }}
                >
                  <Typography variant="p">
                    Number of boats accounted for:{" "}
                    <span style={{ color: "#356DAD", fontWeight: "600" }}>
                      {completeFinishRaceEntrants.length}
                    </span>
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <FormControl
                      size="small"
                      sx={{ maxWidth: 150, minWidth: 100 }}
                    >
                      {" "}
                      {/* fixed width */}
                      <Select
                        startAdornment={
                          <IconButton
                            size="small"
                            onClick={() => setIsSortAsc((prev) => !prev)} // toggle asc/desc
                          >
                            {isSortAsc ? (
                              <ArrowUpwardIcon fontSize="small" />
                            ) : (
                              <ArrowDownwardIcon fontSize="small" />
                            )}
                          </IconButton>
                        }
                        size={"small"}
                        value={sortField}
                        onChange={(e) => setSortField(e.target.value)}
                      >
                        {[
                          ...sortOptions,
                          ...(props.place
                            ? [{ label: "Place", value: "place" }]
                            : [{ label: "Time", value: "time" }]),
                        ].map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      sx={{ maxWidth: 100, minWidth: 100 }}
                      type="search"
                      size="small"
                      variant="outlined"
                      className="table_search_field"
                      placeholder="Sail No."
                      onChange={(e) => {
                        applyFinishedSearch(e.target.value);
                      }}
                    />
                  </Box>
                </Box>
                <div
                  className="ag-theme-alpine"
                  style={{ height: 400, width: "100%" }}
                >
                  <AgGridReact
                    domLayout="normal" // Automatically adjusts grid height based on content
                    ref={gridRefs[2]}
                    context={{ ...gridContext, gridName: "finished" }}
                    className="ty_inlineEdit_datagrid ty_finished_grid ty_grid_focus"
                    columnDefs={finishedColumnDefs}
                    rowData={completeFinishRaceEntrants}
                    headerHeight={36}
                    rowHeight={36}
                    getRowClass={getRowClass} // Apply row-level styling
                    getRowStyle={getRowStyle}
                    stopEditingWhenCellsLoseFocus={true}
                    onCellKeyDown={handleCellKeyPress2}
                    suppressScrollOnNewData={true}
                    defaultColDef={{
                      singleClickEdit: true,
                      sortable: false,
                      filter: false,
                      resizable: false,
                      flex: 1,
                      suppressMovable: true,
                    }}
                    onGridReady={(params) => {
                      params.api.sizeColumnsToFit();
                      const style = document.createElement("style");
                      style.innerHTML = `
                            .ty_inlineEdit_datagrid .ag-header-cell-filter-button {
                              display: none !important;
                            }
                          `;
                      document.head.appendChild(style);
                    }}
                    onGridSizeChanged={(params) => {
                      params.api.sizeColumnsToFit();
                    }}
                  />
                </div>
              </Box>
            </Grid>
            <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    // mb: 2,
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <Typography
                    variant="h6"
                    gutterBottom
                    className="ty_form_heading "
                  >
                    UN FINISHED
                  </Typography>

                  <TextField
                    type="search"
                    size="small"
                    variant="outlined"
                    className="table_search_field"
                    placeholder="Search Sail Number or Boat Name"
                    onChange={(e) => applyUnfinishedSearch(e.target.value)}
                  />
                </Box>

                <div
                  className="ag-theme-alpine"
                  style={{
                    minHeight: 100,
                    // height: 700, // Fixed height 500px
                    maxHeight: 700,
                    width: "100%",
                    height: 800,
                    overflow: "auto", // Enable scrolling
                  }}
                >
                  <AgGridReact
                    domLayout="normal"
                    ref={gridRefs[0]}
                    context={gridContext}
                    className="ty_inlineEdit_datagrid"
                    columnDefs={unfinishedColumnDefs}
                    rowData={filteredUnfinishedEntrants}
                    headerHeight={36}
                    rowHeight={36}
                    stopEditingWhenCellsLoseFocus={true}
                    suppressScrollOnNewData={true}
                    defaultColDef={{
                      sortable: false,
                      draggable: false,
                      filter: true,
                      resizable: false,
                      flex: 1,
                      suppressMovable: true,
                    }}
                    gridOptions={{ enableBrowserTooltips: true }}
                    isExternalFilterPresent={isUnfinishedExternalFilter}
                    doesExternalFilterPass={doesUnfinishedFilterPass}
                    getRowStyle={getRowStyle}
                    onGridReady={(params) => {
                      gridRefs[0].current = params.api;
                      params.api.sizeColumnsToFit();
                    }}
                    onGridSizeChanged={(params) => {
                      params.api.sizeColumnsToFit();
                    }}
                    noRowsOverlayComponentFramework={() => (
                      <div
                        style={{
                          padding: "10px",
                          textAlign: "center",
                          fontSize: "12px",
                          color: "gray",
                        }}
                      >
                        No Data Available
                      </div>
                    )}
                  />
                </div>
              </Box>
              <Box>
                {" "}
                <Typography variant="p">
                  Total Number of boats:{" "}
                  <span style={{ color: "#356DAD", fontWeight: "600" }}>
                    {filteredUnfinishedEntrants.length}
                  </span>
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <StartTimeConfirmModal
        open={showStartTimeModal}
        onClose={handleStartTimeModalClose}
        onProceed={handleProceed}
        onUpdateStartTime={handleUpdateStartTime}
        fleetDetails={props.savedFleetDetails}
      />
    </>
  );
}

const Grid1 = (props) => {
  const stopwatchRef = useRef({
    time: new Date(), // holds current time
    el: null, // will point to the Typography element
  });

  useEffect(() => {
    const timer = setInterval(() => {
      stopwatchRef.current.time = new Date(); // update time

      if (stopwatchRef.current.el) {
        stopwatchRef.current.el.textContent =
          "ABOUT TO FINISH - " + formatTime(stopwatchRef.current.time);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const addRow = () => {
    // Count rows with missing sailNum or boatName
    const emptyRowCount = props.aboutFinishRaceEntrants.filter(
      (row) => !row.sailNum || !row.boatName
    ).length;

    if (emptyRowCount >= 6) return; // Limit to 6 empty rows

    const newRow = {
      id:
        Math.max(
          ...props.unfinishedEntrants.map((r) => r.id),
          ...props.aboutFinishRaceEntrants.map((r) => r.id),
          ...props.completeFinishRaceEntrants.map((r) => r.id),
          0
        ) + 1,
      hr: "00",
      min: "00",
      sec: "00",
      dn: "",
      fleetName: "",
      boatName: "",
      sailNum: "",
      editable: true,
    };

    const updatedAbout = [...props.aboutFinishRaceEntrants, newRow];
    props.dispatch(actions.updateAboutFinishRaceEntrants(updatedAbout));

    // Focus the new row
    setTimeout(() => {
      const api = props.gridRefs[1]?.current?.api;
      if (api) {
        api.startEditingCell({
          rowIndex: updatedAbout.length - 1, // last row index
          colKey: "sailNum",
        });
      }
    }, 100);
  };

  const handleCellKeyPress = (event) => {
    event.event.preventDefault(); // prevent default Enter/Tab behavior

    const key = event.event.key;

    if (key === "Enter") {
      if (event.column.getId() === "remove") {
        handleDelClick(event.data.sailNum, event.data, event, false);
      } else {
        props.handleNowClick(event.data.sailNum, event.data, event, false);
        const currentRowIndex = event.rowIndex;
        const colId = event.column.getId();

        const rowCount = event.api.getDisplayedRowCount();

        if (currentRowIndex < rowCount) {
          event.api.setFocusedCell(currentRowIndex, colId);
          event.api.startEditingCell({
            rowIndex: currentRowIndex,
            colKey: colId,
          });
        }
      }
    }
  };

  const handleDelClick = (cellvalue, celldata, params) => {
    console.log("Now  handleDelClick clicked!", celldata);
    // params.data.buttonDisabled = false;

    let unfinishedEntrants = params.context.unfinishedEntrants;
    let aboutFinishRaceEntrants = params.context.aboutFinishRaceEntrants;
    // let completeFinishRaceEntrants = params.context.completeFinishRaceEntrants;
    console.log("the celll", cellvalue);
    console.log("ENTRANTS", aboutFinishRaceEntrants);
    let aboutFinishRow = aboutFinishRaceEntrants.filter(
      (x) => x.id == celldata.id
    );
    if (aboutFinishRow.length > 0) {
      let aboutFinishRaceEntrants2 = aboutFinishRaceEntrants.filter(
        (x) => x.id != celldata.id
      );

      if (aboutFinishRow[0].boatName) {
        let unfinishedEntrants2 = [...unfinishedEntrants];
        aboutFinishRow[0].hr = "00";
        aboutFinishRow[0].min = "00";
        aboutFinishRow[0].sec = "00";
        aboutFinishRow[0].dn = "";
        aboutFinishRow[0].place = "";
        aboutFinishRow[0].position = "";
        aboutFinishRow[0].editable = false;
        aboutFinishRow[0].errorHR = false;
        aboutFinishRow[0].errorMin = false;
        aboutFinishRow[0].errorSec = false;

        unfinishedEntrants2.push(aboutFinishRow[0]);

        // unfinishedEntrants = unfinishedEntrants2;
        props.dispatch(
          actions.updateInitialFinishRaceEntrants(unfinishedEntrants2)
        );
      }

      aboutFinishRaceEntrants = [...aboutFinishRaceEntrants2];
      props.dispatch(
        actions.updateAboutFinishRaceEntrants(aboutFinishRaceEntrants)
      );
    }
  };

  const aboutToFinishColumnDefs = [
    {
      field: "capture",
      headerName: "CAPTURE",
      flex: 1.5,
      minWidth: 100,
      // maxWidth: 200,
      cellRenderer: (params) => {
        return (
          <Button
            variant="contained"
            onClick={() =>
              props.handleNowClick(
                params.data.sailNum,
                params.data,
                params,
                false
              )
            }
            value={params.value}
            color="primary"
            size="small"
            className="ty_primary_btn finish_time_now_btn"
            disabled={params.data?.isAbandon}
          >
            NOW
          </Button>
        );
      },
    },
    {
      field: "sailNum",
      headerName: "SAIL NO",
      flex: 2,
      minWidth: 100,
      maxWidth: 200,
      valueParser: (params) => params.newValue?.toUpperCase() || "",
      editable: (params) => !params.data?.boatName && !params.data?.isAbandon,
      cellClassRules: {
        "highlight-cell": (params) =>
          params.data?.boatName !== null && params.data?.boatName !== "",
      },
      cellStyle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        overflow: 'hidden',
      },
      cellRenderer: (params) => {
        const isManualEntry = !params.data?.boatName || params.data?.boatName === "";
        return (
          <div
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%',
              // padding: '0 12px',
              fontWeight: isManualEntry ? '700' : 'normal',
            }}
            title={params.value || ''}
          >
            {params.value || ''}
          </div>
        );
      },

      onCellKeyDown: (event) => {
        console.log("Enter/Tab pressed in SailNum:", event);
        if (
          event.colDef.field === "sailNum" &&
          (event.event.key === "Enter" || event.event.key === "Tab")
        ) {
          event.event.preventDefault();
          console.log("Enter/Tab pressed in SailNum:", event);

          event.api.stopEditing();
          let unfinishedEntrants = event.context.unfinishedEntrants;
          let aboutFinishRaceEntrants = event.context.aboutFinishRaceEntrants;

          if (event.data.boatName) {
            console.warn(
              "Edit prevented because boatName is set:",
              event.data.boatName
            );
            event.node.setDataValue(event.colDef.field, event.oldValue); // Revert change
            return;
          }
          let unFinishRow = unfinishedEntrants.filter(
            (x) => x.sailNum.toLowerCase() === event.newValue.toLowerCase()
          );
          if (unFinishRow.length > 0) {
            console.log("the fetched", unFinishRow[0]);
            let unfinishedEntrants2 = unfinishedEntrants.filter(
              (x) => x.sailNum.toLowerCase() !== event.newValue.toLowerCase()
            );

            let aboutFinishRaceEntrants2 = aboutFinishRaceEntrants.filter(
              (x) => x.id != event.data.id
            );

            aboutFinishRaceEntrants2.push(unFinishRow[0]);

            console.log("aboutFinishRaceEntrants", aboutFinishRaceEntrants);
            unfinishedEntrants = [...unfinishedEntrants2];
            props.dispatch(
              actions.updateInitialFinishRaceEntrants(unfinishedEntrants)
            );
            console.log("aboutFinishRaceEntrants2", aboutFinishRaceEntrants2);

            aboutFinishRaceEntrants = [...aboutFinishRaceEntrants2];
            props.dispatch(
              actions.updateAboutFinishRaceEntrants(aboutFinishRaceEntrants)
            );
          }

          props.dispatch(
            actions.updateFinishRaceManager([
              ...unFinishRow,
              ...aboutFinishRaceEntrants,
              ...props.completeFinishRaceEntrants,
            ])
          );
        }
      },
    },
    {
      field: "boatName",
      headerName: "BOAT NAME",
      flex: 2,
      minWidth: 100,
      maxWidth: 200,
      hide: true,
      cellStyle: {
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "block",
      },
      cellRenderer: (params) => (
        <span title={params.value || ''}>{params.value}</span>
      ),
    },
    {
      field: "remove",
      headerName: "DROP",
      flex: 1.5,
      minWidth: 100,
      maxWidth: 200,
      cellRenderer: (params) => {
        return (
          <Button
            onClick={() =>
              handleDelClick(params.data.sailNum, params.data, params)
            }
            value={params.value}
            color="primary"
            className="ty_cross_btn"
            disabled={params.data?.isAbandon}
          >
            ×
          </Button>
        );
      },
    },
  ];

  const rowCount = props.aboutFinishRaceEntrants.length;
  const totalHeight = headerHeight + rowCount * rowHeight;
  const gridHeight = Math.min(Math.max(totalHeight, minHeight), maxHeight);

  return (
    <Box>
      <Typography
        variant="h6"
        gutterBottom
        className="ty_form_heading"
        ref={(el) => (stopwatchRef.current.el = el)} // set DOM node inside the ref object
      >
        ABOUT TO FINISH - {formatTime(stopwatchRef.current.time)}{" "}
        {/* only used for first render */}
      </Typography>
      <div
        className="ag-theme-alpine"
        style={{
          height: gridHeight,
          overflow: "auto",
        }}
      >
        <AgGridReact
          domLayout="normal" // Automatically adjusts grid height based on content
          ref={props.gridRefs[1]}
          context={props.gridContext}
          className="ty_inlineEdit_datagrid ty_about_to_finish ty_grid_focus"
          columnDefs={aboutToFinishColumnDefs}
          editable={true}
          headerHeight={36}
          rowHeight={36}
          rowData={props.aboutFinishRaceEntrants}
          stopEditingWhenCellsLoseFocus={true}
          suppressScrollOnNewData={true}
          defaultColDef={{
            sortable: false,
            filter: false,
            resizable: false,
            flex: 1,
            singleClickEdit: true,
            suppressMovable: true,
          }}
          isCellEditable={(params) => params.data.isAbandon}
          getRowStyle={getRowStyle}
          gridOptions={{
            enableBrowserTooltips: true,
            suppressKeyboardEvent: (params) => params.event.key === "Tab",
          }}
          onCellKeyDown={handleCellKeyPress}
          onGridReady={(params) => {
            params.api.sizeColumnsToFit();
          }}
          onGridSizeChanged={(params) => {
            params.api.sizeColumnsToFit();
          }}
        />
      </div>
      <Button
        fullWidth
        className="ty_secondary_btn addcaptureButton"
        sx={{
          border: "1px dashed var(--ty-primary-color) !important",
        }}
        onClick={addRow}
        disabled={
          props.aboutFinishRaceEntrants.filter(
            (row) => !row.sailNum || !row.boatName
          ).length >= 6
        } //  Button disables when 6 empty rows exist
      >
        ADD CAPTURE ROW
      </Button>
    </Box>
  );
};
